import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { AnalyticsCupNav } from './components/AnalyticsCupNav';
import './AnalyticsCup.scss';

import videosData from '../../assets/analytics_cup/analytics-cup-26-videos.json';
import submissionsCSV from '../../assets/analytics_cup/analytics-cup-26.csv';

// ── CSV helpers ────────────────────────────────────────────────────────────────

const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQuotes = !inQuotes; }
        else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
        else { current += ch; }
    }
    result.push(current.trim());
    return result;
};

const parseCSV = (text) =>
    text.trim().split('\n').slice(1).map(line => {
        const v = parseCSVLine(line);
        return {
            id: v[0], title: v[1], track: v[2], name: v[3],
            finalist: v[4] === 'TRUE',
            honourableMention: v[5] === 'TRUE',
            dataUsed: v[6], github: v[7],
        };
    });

const normalizeName = (name = '') =>
    name.toLowerCase().replace(/[_\s]+/g, '');

const capitalizeName = (name = '') =>
    name.toLowerCase().split(/[\s_]+/)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');

// ── Sub-components ─────────────────────────────────────────────────────────────

const TrackBadge = ({ track }) => {
    const slug = track === 'Research Track' ? 'research' : 'analyst';
    return <span className={`ac-submission-card__track ac-submission-card__track--${slug}`}>{track}</span>;
};

const BadgePill = ({ badge }) => (
    <span className={`ac-submission-card__badge ac-submission-card__badge--${badge}`}>
        {badge === 'winner' ? 'Winner' : 'Finalist'}
    </span>
);

const GitHubLink = ({ url, label = 'View on GitHub' }) => (
    <a href={url} target="_blank" rel="noopener noreferrer" className="ac-video__github-link">
        {label}
    </a>
);

// ── Featured (winner) video ────────────────────────────────────────────────────

const FeaturedVideo = ({ video, submission }) => {
    const displayName = submission
        ? capitalizeName(submission.name)
        : capitalizeName(video.nameKey);

    return (
        <div className="ac-videos__featured">
            <div className="ac-videos__featured-embed">
                {video.youtubeId ? (
                    <div className="ac-video__ratio">
                        <iframe
                            src={`https://www.youtube.com/embed/${video.youtubeId}`}
                            title={`${displayName} — ${video.title}`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                ) : (
                    <div className="ac-video__ratio ac-video__ratio--placeholder">
                        <span>Video coming soon</span>
                    </div>
                )}
            </div>

            <div className="ac-videos__featured-info">
                <div className="ac-videos__featured-badges">
                    <BadgePill badge={video.badge} />
                    <TrackBadge track={video.track} />
                </div>
                <h2 className="ac-videos__featured-name">{displayName}</h2>
                <p className="ac-videos__featured-title">{video.title}</p>
                <GitHubLink url={video.github} label="View project on GitHub" />
            </div>
        </div>
    );
};

// ── Finalist video card ────────────────────────────────────────────────────────

const VideoCard = ({ video, submission }) => {
    const displayName = submission
        ? capitalizeName(submission.name)
        : capitalizeName(video.nameKey);

    const thumbUrl = video.youtubeId
        ? `https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`
        : null;

    return (
        <div className="ac-video-card">
            {video.youtubeId ? (
                <a
                    href={`https://www.youtube.com/watch?v=${video.youtubeId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ac-video-card__thumb-link"
                    aria-label={`Watch ${displayName}`}
                >
                    <div className="ac-video-card__thumb">
                        <img src={thumbUrl} alt={displayName} />
                        <div className="ac-video-card__play">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="40" height="40" aria-hidden="true">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </div>
                    </div>
                </a>
            ) : (
                <div className="ac-video-card__thumb ac-video-card__thumb--empty">
                    <span>Video coming soon</span>
                </div>
            )}

            <div className="ac-video-card__info">
                <div className="ac-video-card__badges">
                    <TrackBadge track={video.track} />
                    <BadgePill badge={video.badge} />
                </div>
                <h3 className="ac-video-card__name">{displayName}</h3>
                <p className="ac-video-card__title">{video.title}</p>
                <GitHubLink url={video.github} />
            </div>
        </div>
    );
};

// ── Page ───────────────────────────────────────────────────────────────────────

export const AnalyticsCupVideos = () => {
    const [submissions, setSubmissions] = useState([]);

    useEffect(() => {
        fetch(submissionsCSV)
            .then(r => r.text())
            .then(text => setSubmissions(parseCSV(text)))
            .catch(() => {});
    }, []);

    const findSubmission = (nameKey) =>
        submissions.find(s => normalizeName(s.name) === normalizeName(nameKey)) || null;

    const winner = videosData.videos.find(v => v.badge === 'winner');
    const finalists = videosData.videos.filter(v => v.badge !== 'winner');

    return (
        <div className="analytics-cup">
            <Helmet>
                <title>Finalist Presentations — Analytics Cup | PySport</title>
                <meta name="description" content="Watch the Analytics Cup 1.0 finalist presentations from the live event in Paris." />
            </Helmet>
            <AnalyticsCupNav />

            <div className="ac-videos-page">
                <div className="container">

                    <div className="ac-submissions-page__header">
                        <h1 className="ac-submissions-page__title">Presentations</h1>
                        <p className="ac-submissions-page__sub">
                            {videosData.eventLocation} · {videosData.eventDate}
                        </p>
                        <a
                            href={videosData.eventUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ac-results__all-link"
                        >
                            View event page
                        </a>
                    </div>

                    {winner && (
                        <section className="ac-videos__section">
                            <FeaturedVideo
                                video={winner}
                                submission={findSubmission(winner.nameKey)}
                            />
                        </section>
                    )}

                    {finalists.length > 0 && (
                        <section className="ac-videos__section">
                            <div className="ac-videos__grid">
                                {finalists.map(v => (
                                    <VideoCard
                                        key={v.nameKey}
                                        video={v}
                                        submission={findSubmission(v.nameKey)}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                </div>
            </div>
        </div>
    );
};
